import combineEventHandlers from '@atlas/tool/box/event/combineEventHandlers';
import type { THotkey } from '@atlas/tool/box/event/hotkey';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import Option, { type TOptionProps, type TOptionPropsTooltip, type TOptionRef } from '@atlas/uicorn/lib/Control/Option';
import HotkeyLabel from '@atlas/uicorn/lib/Display/HotkeyLabel';
import Text from '@atlas/uicorn/lib/Display/Text';
import TickIcon from '@atlas/uicorn/lib/Icon/TickIcon';
import Checkbox from '@atlas/uicorn/lib/Input/Checkbox';
import type { TExample } from '@atlas/uicorn/preview/modules';
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import styled, { type RuleSet, css, type WebTarget } from 'styled-components';
import useRefCallback from 'use-ref-callback';
import DndContext from '@atlas/uicorn/lib/Dnd/DndContext';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Modifier } from '@dnd-kit/core';

function OptionsListComponent<TOption extends TOptionsListItem>(
    props: TOptionsListProps<TOption>,
    ref: React.ForwardedRef<TOptionsListRef>,
): React.ReactElement {
    const {
        items: propsItems,
        disabled,
        onChoose,
        onSelect,
        onSort,
        defaultSelected,
        selected: propsSelected,
        cursor: propsCursor,
        onCursorChange,
        defaultCursor,
        keepCursor,
        horizontal,
        inlineDescription,
        ...elementProps
    } = props;

    const items = useMemo(
        () =>
            propsItems.sort((a, b) => {
                if ((a.group || false) !== (b.group || false)) return 0;
                if ((a.start || false) !== (b.start || false)) return b.start ? 1 : -1;
                if ((a.end || false) !== (b.end || false)) return a.end ? 1 : -1;
                return 0;
            }),
        [propsItems],
    );

    const isOptionAvailable = useCallback(
        (item: TOptionsListItem) => !(typeof item.disabled === 'boolean' ? item.disabled : disabled),
        [disabled],
    );

    // Option key that is currently focused by keyboard or API
    const [cursor, setCursorKey] = useControlledState<TOption['key'] | null>(
        propsCursor,
        onCursorChange,
        defaultCursor,
    );

    const [selected, setSelected] = useControlledState<TOptionsListItemKey[]>(
        propsSelected,
        undefined,
        defaultSelected ?? [],
    );

    useEffect(
        function resetSelection() {
            if (!onSelect) return setSelected((selected) => (selected.length ? [] : selected));
            else if (defaultSelected?.length) setSelected(defaultSelected);
        },
        [onSelect],
    );

    useLayoutEffect(
        function resetCursor() {
            if (cursor === null && !keepCursor) return;

            const availableOptions = items.filter(isOptionAvailable);
            const cursorIndex = availableOptions.findIndex((item) => item.key === cursor);

            if (keepCursor !== 'top' && cursorIndex !== -1) return; // Cursor is in the list pointing to available option

            if (keepCursor) {
                setCursorKey(availableOptions[0]?.key ?? null);
            } else {
                setCursorKey(null);
            }
        },
        [items, keepCursor, isOptionAvailable],
    );

    // Prevent transition when items change
    useLayoutEffect(() => {
        menuRef.current?.classList.add(noTransitionClass);

        const classList = menuRef.current?.classList;

        const timeout = setTimeout(() => {
            classList?.remove(noTransitionClass);
        }, 50);

        return () => {
            classList?.remove(noTransitionClass);
            clearTimeout(timeout);
        };
    }, [items]);

    const menuRef = useRef<HTMLDivElement>(null);

    const getOptionElement = useRefCallback<TOptionsListRef['getOptionElement']>((key) => {
        return menuRef.current?.querySelector<TOptionRef>(`[${keyAttribute}='${key}']`) ?? null;
    });

    const removeCursor = useCallback<TOptionsListRef['removeCursor']>(() => setCursorKey(null), []);

    const setMenuRef = useRefCallback((element: HTMLMenuElement | null) => {
        menuRef.current?.removeEventListener('focusout', removeCursor);
        const htmlElement =
            element === null ? null : element instanceof HTMLElement ? element : (element as any).scrollContainer;
        htmlElement?.addEventListener('focusout', removeCursor);
        Object.assign(menuRef, { current: htmlElement });
    });

    const setCursor = useRefCallback<TOptionsListRef['setCursor']>((key) => {
        const availableItems = items.filter(isOptionAvailable);

        if (typeof key === 'function') {
            const cursorIndex = cursor === null ? -1 : items.findIndex((item) => item.key === cursor);
            key = key(availableItems, cursorIndex);
        }

        if (key === null) {
            setCursorKey(null);
            return null;
        }

        if (typeof key === 'undefined' || availableItems.every((item) => item.key !== key)) {
            if (cursor) return cursor;
            setCursorKey(availableItems[0]?.key ?? null);
            return availableItems[0]?.key ?? null;
        }

        setCursorKey(key);
        getOptionElement(key)?.scrollIntoView({ block: 'nearest' });

        return key;
    });

    const moveCursorToPrevious = useRefCallback<TOptionsListRef['moveCursorToPrevious']>(() =>
        setCursor((availableItems, cursorIndex) => {
            if (disabled) return null;

            const orderedItems = ~cursorIndex
                ? availableItems
                      .slice(cursorIndex + 1)
                      .concat(items.slice(0, cursorIndex))
                      .reverse()
                : availableItems.slice(0).reverse();

            const newFocusKey = orderedItems.find(isOptionAvailable)?.key;
            if (!newFocusKey) return null;
            return newFocusKey;
        }),
    );

    const moveCursorToNext = useRefCallback<TOptionsListRef['moveCursorToNext']>(() =>
        setCursor((availableItems, cursorIndex) => {
            if (disabled) return null;

            const orderedItems = ~cursorIndex
                ? availableItems.slice(cursorIndex + 1).concat(items.slice(0, cursorIndex))
                : availableItems;

            const newFocusKey = orderedItems.find(isOptionAvailable)?.key;
            if (!newFocusKey) return null;
            return newFocusKey;
        }),
    );

    const selectOption = useRefCallback((option: TOption) => {
        if (!onSelect) return;
        if (option.selectable === false) return;
        setSelected(function toggleSelectedOption(state) {
            const index = state.findIndex((selectedKey) => selectedKey === option.key);
            const newSelected = ~index
                ? state.slice(0, index).concat(state.slice(index + 1))
                : state.concat(option.key);

            onSelect(newSelected, option.key, index === -1);

            return newSelected;
        });
    });

    const chooseOption = useRefCallback(async (option: TOption, selection: boolean) => {
        setCursorKey(option.key);

        const resultOption = option.lazy ? ((await option.lazy(option)) as TOption | null) : option;

        if (!resultOption) return;

        if (!option.lazy) selectOption(resultOption);
        resultOption.onChoose?.(selection);
        onChoose?.(resultOption, selection);
    });

    const chooseCursor = useRefCallback<TOptionsListRef['chooseCursor']>(() => {
        const focusedOption = items.find((item) => item.key === cursor);
        if (!focusedOption) return;
        chooseOption(focusedOption, false);
    });

    const selectCursor = useRefCallback<TOptionsListRef['selectCursor']>(() => {
        const focusedOption = items.find((item) => item.key === cursor);
        if (!focusedOption) return;
        selectOption(focusedOption);
    });

    useImperativeHandle(
        ref,
        () => ({
            removeCursor,
            setCursor,
            moveCursorToPrevious,
            moveCursorToNext,
            chooseCursor,
            selectCursor,
            getOptionElement,
        }),
        [],
    );

    const hasSelection = Boolean(onSelect);

    const preparedItems = useMemo(
        () =>
            items.map<
                React.ComponentProps<typeof SOptionsListButton<WebTarget>> & {
                    key: TOption['key'];
                    group?: string;
                    end?: boolean;
                    sortable?: boolean;
                }
            >((item) => {
                const label = item.renderLabel ? item.renderLabel(item) : item.label;
                const icon = typeof item.icon === 'function' ? item.icon(item) : item.icon;
                return {
                    key: item.key,
                    className: item.className,
                    group: item.group,
                    pushSuffix: true,
                    noFocusStyle: true,
                    noHoverStyle: true,
                    [cursorAttribute]: (cursor === item.key).toString(),
                    [keyAttribute]: item.key,
                    tooltip: item.tooltip,
                    sortable: item.sortable,
                    ellipsis: item.ellipsis,
                    href: item.href,
                    children: (
                        <>
                            {typeof icon !== 'undefined' && icon !== null && <span>{icon}</span>}
                            {isNotEmpty(label) || isNotEmpty(item.description) ? (
                                <SOptionsListItemText $inline={inlineDescription}>
                                    {isNotEmpty(label) && (
                                        <Text p ellipsis>
                                            {label}
                                            {item.labelSuffix}
                                        </Text>
                                    )}
                                    {isNotEmpty(item.description) && (
                                        <SOptionListItemDescription children={item.description} />
                                    )}
                                </SOptionsListItemText>
                            ) : null}
                        </>
                    ),
                    prefix:
                        hasSelection && (typeof item.selectable === 'undefined' || item.selectable) ? (
                            <>
                                <Checkbox
                                    tabIndex={-1}
                                    checked={selected.includes(item.key)}
                                    disabled={item.disabled || !!item.lazy}
                                    onChange={noop}
                                    onClick={noop}
                                />
                                {item.prefix}
                            </>
                        ) : (
                            item.prefix
                        ),
                    suffix:
                        (!hasSelection && selected.includes(item.key)) || item.suffix || item.hotkey ? (
                            <>
                                {!hasSelection && selected.includes(item.key) ? <TickIcon size={10} /> : null}
                                {item.suffix}
                                {item.hotkey && <HotkeyLabel hotkey={item.hotkey} />}
                            </>
                        ) : null,
                    disabled: disabled || item.disabled,
                    danger: item.danger || false,
                    lazy: item.lazy,
                    end: item.end,
                    onClick: combineEventHandlers(item.onClick, (event) =>
                        chooseOption(item, (event.target as HTMLInputElement).type === 'checkbox'),
                    ),
                    onMouseEnter: item.onMouseEnter,
                    onMouseLeave: item.onMouseLeave,
                    onKeyDown: item.onKeyDown,
                    onFocus: combineEventHandlers(item.onFocus, () => setCursor(item.key)),
                };
            }),
        [items, selected, disabled, hasSelection, cursor, inlineDescription],
    );

    const hasSortHandler = typeof onSort === 'function';
    const isSortableList = useMemo(
        () => hasSortHandler && items.some((item) => item.sortable),
        [items, hasSortHandler],
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            if (!onSort || !event.over || event.active.id === event.over.id) return;

            const activeId = String(event.active.id);
            const overId = String(event.over.id);

            const oldIndex = preparedItems.findIndex((item) => String(item.key) === activeId);
            const newIndex = preparedItems.findIndex((item) => String(item.key) === overId);

            if (oldIndex === -1 || newIndex === -1) return;

            onSort({ oldIndex, newIndex, collection: 0 });
        },
        [onSort, preparedItems],
    );

    const itemsKeys = useMemo(() => preparedItems.map((item) => String(item.key)), [preparedItems]);

    const content = (
        <SOptionsList {...elementProps} $horizontal={horizontal} ref={setMenuRef}>
            {preparedItems.map(({ key, group, end, sortable, ...item }, index, list) => {
                return (
                    <React.Fragment key={(group ?? '') + key}>
                        {end && !list[index - 1]?.end ? <SOptionsListSpacer /> : null}
                        {group && list[index - 1]?.group !== group ? <SOptionsListGroup children={group} /> : null}
                        {isSortableList && sortable ? (
                            <SortableListItem
                                id={String(key)}
                                canDrag={true}
                                children={<SOptionsListButton {...item} aria-label={item['aria-label']?.toString()} />}
                            />
                        ) : (
                            <SOptionsListItem
                                children={<SOptionsListButton {...item} aria-label={item['aria-label']?.toString()} />}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </SOptionsList>
    );

    if (!isSortableList) return content;

    return (
        <DndContext
            onDragEnd={handleDragEnd}
            modifiers={[horizontal ? restrictToHorizontalAxis : restrictToVerticalAxis]}
        >
            <SortableContext
                items={itemsKeys}
                strategy={horizontal ? horizontalListSortingStrategy : verticalListSortingStrategy}
            >
                {content}
            </SortableContext>
        </DndContext>
    );
}

const OptionsList = React.memo(React.forwardRef(OptionsListComponent)) as <TOption extends TOptionsListItem>(
    props: TOptionsListProps<TOption> & { ref?: React.Ref<TOptionsListRef> },
) => React.ReactElement;

export default OptionsList;

// #region Helpers

const noTransitionClass = 'no-transition';
const cursorAttribute = 'data-cursor';
const keyAttribute = 'aria-label';

const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 });
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

function noop() {}

function isNotEmpty(value: undefined | null | string | number | React.ReactNode): boolean {
    if (value === null) return false;
    if (typeof value === 'undefined') return false;
    if (typeof value === 'number') return !isNaN(value);
    if (typeof value === 'string') return value.length > 0;
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.length > 0;
    return true;
}

// #endregion

// #region Styles

export const optionListItemEffectStyles = (styles: RuleSet) => css`
    &:is([${cursorAttribute}='true']),
    &:is(:not(:disabled):hover) {
        ${styles}
    }
`;

export const optionListItemActiveStyles = (styles: RuleSet) => css`
    &:is([${cursorAttribute}='true']) {
        ${styles}
    }
`;

export const SOptionsListButton = styled(Option)`
    padding: calc(var(--options-list-item-spacing) / 2) var(--options-list-item-spacing);
    width: 100%;

    &:focus-visible {
        outline: none;
    }

    ${optionListItemEffectStyles(
        css`
            background-color: var(--options-list-hover-color);
        `,
    )}
`;

const SOptionsListSpacer = styled.li`
    flex: 1;
`;

export const SOptionsListGroup = styled.li`
    font: ${(p) => p.theme.font.caption};
    color: ${(p) => p.theme.color.grey8};
    padding: var(--options-list-item-spacing) calc(var(--options-list-item-spacing) * 2) 0
        var(--options-list-item-spacing);

    position: sticky;
    top: 0;
    z-index: 1;
    background: inherit;
`;

export const SOptionsListItem = styled.li<{
    $transform?: { x: number; y: number } | null;
    $transition?: string;
    $isDragging?: boolean;
}>`
    list-style: none;
    margin: 0;
    transform: ${(p) => (p.$transform ? `translate3d(${p.$transform.x}px, ${p.$transform.y}px, 0)` : 'none')};
    transition: ${(p) => p.$transition || 'none'};
    opacity: ${(p) => (p.$isDragging ? 0.5 : 1)};
`;

function SortableListItem({
    id,
    canDrag,
    children,
    ...props
}: Omit<React.HTMLAttributes<HTMLLIElement>, 'canDrag'> & { id: string | number; canDrag: boolean }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: String(id),
        disabled: !canDrag,
    });

    return (
        <SOptionsListItem
            ref={setNodeRef}
            $transform={transform}
            $transition={transition}
            $isDragging={isDragging}
            {...(canDrag ? { ...attributes, ...listeners } : {})}
            {...props}
        >
            {children}
        </SOptionsListItem>
    );
}

export const SOptionsList = styled.menu<{ $horizontal?: boolean }>`
    --options-list-item-spacing: ${(p) => (p.$horizontal ? '0.8rem' : '0.4rem')};
    --options-list-hover-color: ${(p) => p.theme.color.grey51};

    &.${noTransitionClass} ${SOptionsListButton} {
        transition: none;
    }

    font: ${(p) => p.theme.font.normal};
    color: ${(p) => p.theme.color.grey9};
    border-radius: ${(p) => p.theme.radius.small};
    background-color: ${(p) => p.theme.color.grey0};

    list-style: none;
    padding: 0;
    margin: 0;
    overflow: auto;

    display: flex;
    gap: var(--options-list-item-spacing);
    flex-direction: ${(p) => (p.$horizontal ? 'row' : 'column')};
    align-items: ${(p) => (p.$horizontal ? 'center' : 'stretch')};

    > ${SOptionsListSpacer} {
        ${(p) =>
            p.$horizontal
                ? css`
                      margin-right: calc(-1 * var(--options-list-item-spacing));
                  `
                : css`
                      margin-bottom: calc(-1 * var(--options-list-item-spacing));
                  `}
    }
`;

export const SOptionListItemDescription = styled.span`
    font: ${(p) => p.theme.font.caption};
    color: ${(p) => p.theme.color.grey8};
`;

export const SOptionsListItemText = styled.span<{ $inline?: boolean }>`
    ${(p) => p.theme.overflowText()};

    ${(p) =>
        p.$inline
            ? css`
                  display: inline-flex;
                  align-items: inherit;
                  gap: inherit;
              `
            : ''}
`;

// #endregion

// #region Types

export type TOptionsListItemKey = string | number;

export type TOptionsListChooseHandler<TOption extends TOptionsListItem = TOptionsListItem> = (
    option: TOption,
    selection: boolean,
) => void;

type TOptionsListSelectHandler<TOption extends TOptionsListItem = TOptionsListItem> = (
    selectedKeys: TOption['key'][],
    keyInAction: TOption['key'],
    addedToSelection: boolean,
) => void;

export type TOptionsListItemTooltip = TOptionPropsTooltip;

export type TOptionsListItem = {
    key: TOptionsListItemKey;
    className?: string;

    tooltip?: TOptionsListItemTooltip;

    disabled?: boolean;
    selected?: boolean;
    selectable?: boolean;
    danger?: boolean;

    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    labelSuffix?: React.ReactNode;

    icon?: React.ReactNode | ((option: TOptionsListItem) => React.ReactNode);
    label: string;
    renderLabel?: (option: TOptionsListItem) => React.ReactNode;
    description?: string;
    hotkey?: THotkey;
    ellipsis?: boolean;

    lazy?: (option: TOptionsListItem) => void | TOptionsListItem | null | Promise<TOptionsListItem | null>;

    href?: string;
    onChoose?: (selection: boolean) => void;

    start?: boolean;
    end?: boolean;
    sortable?: boolean;
    group?: string;

    onClick?: TOptionProps['onClick'];
    onMouseEnter?: TOptionProps['onMouseEnter'];
    onMouseLeave?: TOptionProps['onMouseLeave'];
    onKeyDown?: TOptionProps['onKeyDown'];
    onFocus?: TOptionProps['onFocus'];
};

export type TOptionsListRef<TOption extends TOptionsListItem = TOptionsListItem> = {
    removeCursor(): void;
    setCursor(
        key?:
            | null
            | TOptionsListItemKey
            | ((availableItems: TOption[], cursorIndex: number) => null | TOptionsListItemKey),
    ): TOptionsListItemKey | null;
    moveCursorToPrevious(): TOptionsListItemKey | null;
    moveCursorToNext(): TOptionsListItemKey | null;
    chooseCursor(): void;
    selectCursor(): void;
    getOptionElement(key: TOptionsListItemKey): TOptionRef | null;
};

export type TOptionsListElementProps = Omit<React.HTMLAttributes<HTMLMenuElement>, 'onSelect'>;

export type TOptionsListSortHandler = (params: { oldIndex: number; newIndex: number; collection?: number }) => void;

export type TOptionsListProps<TOption extends TOptionsListItem = TOptionsListItem> = TOptionsListElementProps & {
    items: TOption[];
    disabled?: boolean;
    onChoose?: TOptionsListChooseHandler<TOption>;
    onSelect?: TOptionsListSelectHandler<TOption>;
    defaultSelected?: TOption['key'][];
    selected?: TOption['key'][];
    onSort?: TOptionsListSortHandler;
    cursor?: TOption['key'];
    onCursorChange?: (cursor: TOption['key'] | null) => void;
    defaultCursor?: string;
    keepCursor?: boolean | 'top';
    horizontal?: boolean;
    inlineDescription?: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    containerStyle: {
        border: '2px solid green',
    },
    render(props) {
        const [mining, setMining] = useState(false);
        const [darkMode, setDarkMode] = useState(false);

        const sortable = props.sortable || false;

        const ref = useRef<TOptionsListRef>(null);

        props.moveCursorToPrevious?.(() => ref.current?.moveCursorToPrevious());
        props.moveCursorToNext?.(() => ref.current?.moveCursorToNext());
        props.setCursor?.(() => ref.current?.setCursor());
        props.setCursorOnChat?.(() => ref.current?.setCursor('chat'));
        props.chooseCursor?.(() => ref.current?.chooseCursor());
        props.selectCursor?.(() => ref.current?.selectCursor());
        props.removeCursor?.(() => ref.current?.removeCursor());

        const style: React.CSSProperties = {
            ...(props.limitHeight ? { height: 200 } : props.withEnding ? { height: 400 } : undefined),
            ...(props.background && { background: props.background, boxShadow: '0 0 0.1rem 0rem #999' }),
        };

        return (
            <OptionsList
                ref={ref}
                disabled={props.disabled}
                onChoose={props.onChoose}
                onSelect={props.selectable ? props.onSelect : undefined}
                onSort={sortable ? props.onSort : undefined}
                keepCursor={props.keepCursor}
                horizontal={props.horizontal}
                style={style}
                items={[
                    {
                        key: 'new',
                        label: 'New',
                        suffix: (
                            <div
                                style={{
                                    width: '1.8rem',
                                    height: '1.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'red',
                                    color: '#fff',
                                    borderRadius: '50%',
                                }}
                            >
                                3
                            </div>
                        ),
                        sortable,
                    },
                    { key: 'all', label: 'All', sortable },
                    { key: 'profile', label: 'Profile', sortable },
                    { key: 'chat', label: 'Chat', sortable },
                    {
                        key: 'mining',
                        label: mining ? 'Stop mining' : 'Start mining',
                        disabled: false,
                        onChoose: () => setMining((v) => !v),
                        group: 'Interactive',
                        sortable,
                    },
                    {
                        key: 'greeting',
                        prefix: '👋',
                        label: 'Hello!',
                        tooltip: 'Click prevented',
                        onChoose: () => {
                            alert('Hi you back!');
                        },
                        group: 'Interactive',
                        sortable,
                    },
                    {
                        key: 'darkMode',
                        label: 'Dark Mode',
                        suffix: darkMode ? '✔︎' : null,
                        onChoose: () => setDarkMode((d) => !d),
                        group: 'Interactive',
                        sortable,
                    },
                    {
                        key: 'gpt',
                        label: 'GPT (unavailable)',
                        suffix: '🪄',
                        disabled: true,
                        group: 'Interactive',
                        sortable,
                    },
                    {
                        key: 'custom',
                        label: 'Custom (lazy)',
                        group: 'Interactive',
                        lazy: () =>
                            new Promise((resolve) =>
                                // TODO: Prevent choose if unmounted
                                setTimeout(resolve, 1e3, { key: 'custom', children: 'Lazily selected' }),
                            ),
                        end: props.withEnding,
                        sortable,
                    },
                    {
                        key: 'delete',
                        label: 'Delete',
                        group: 'Interactive',
                        danger: true,
                        end: props.withEnding,
                        sortable,
                    },
                ]}
            />
        );
    },
    controls: {
        moveCursorToPrevious: 'callback',
        moveCursorToNext: 'callback',
        setCursor: 'callback',
        setCursorOnChat: 'callback',
        chooseCursor: 'callback',
        selectCursor: 'callback',
        removeCursor: 'callback',
        selectable: 'boolean',
        disabled: 'boolean',
        limitHeight: { type: 'boolean', initial: false, required: true },
        keepCursor: 'boolean',
        horizontal: 'boolean',
        withEnding: 'boolean',
        sortable: 'boolean',
    },
    handlers: {
        onChoose: 'console',
        onSelect: 'console',
        onSort: 'console',
    },
    figma: [
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=21-1284&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-7700&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=52-5474&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=43-6969&mode=design&t=TVklSB9u5fm4OQmw-0',
        'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=28-6083&mode=design&t=TVklSB9u5fm4OQmw-0',
    ],
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('OptionsList', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
